import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Loader2, Save, ShoppingBag, PackageCheck, X } from 'lucide-react';
import { api } from '@/utils/api';
import { formatINR } from '@/utils/formatters';
import { ProductGridSelector, type ProductGridItem } from '@/components/shared/ProductGridSelector';

interface Vendor { id: string; name: string; gstin?: string | null; }
interface LineItem {
  description: string;
  hsnSac: string;
  quantity: number;
  rate: number;
  gstRate: number;
  discount: number;
  productId: string;
}

interface GrnItem {
  id: string;
  description: string;
  orderedQty: number;
  receivedQty: number;
}

const GST_RATES = [0, 5, 12, 18, 28];

const emptyItem = (): LineItem => ({ description: '', hsnSac: '', quantity: 1, rate: 0, gstRate: 18, discount: 0, productId: '' });

export function PurchaseOrderEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('draft');
  const [vendors, setVendors] = useState<Vendor[]>([]);

  // GRN modal state
  const [showGrn, setShowGrn] = useState(false);
  const [grnItems, setGrnItems] = useState<GrnItem[]>([]);
  const [grnNotes, setGrnNotes] = useState('');
  const [grnSaving, setGrnSaving] = useState(false);

  const [vendorId, setVendorId] = useState('');
  const [vendorGstin, setVendorGstin] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [notes, setNotes] = useState('');
  const [billingAddress, setBillingAddress] = useState<any>({ line1: '', line2: '', city: '', state: '', pincode: '', country: 'India' });
  const [deliveryAddress, setDeliveryAddress] = useState<any>({ line1: '', line2: '', city: '', state: '', pincode: '', country: 'India' });
  const [pickupAddress, setPickupAddress] = useState<any>({ line1: '', line2: '', city: '', state: '', pincode: '', country: 'India' });
  const [items, setItems] = useState<LineItem[]>([emptyItem()]);
  const [showProducts, setShowProducts] = useState(false);

  // Document-level vendor discount
  const [docDiscountType, setDocDiscountType] = useState<'amount' | 'percentage'>('amount');
  const [docDiscountValue, setDocDiscountValue] = useState<number>(0);

  const handleProductSelect = (product: ProductGridItem) => {
    const newItem: LineItem = {
      description: product.name,
      hsnSac: product.hsnCode || '',
      quantity: 1,
      rate: product.unitPrice || 0,
      gstRate: product.gstRate ?? 18,
      discount: 0,
      productId: product.id,
    };
    // Replace first empty item or append
    const firstEmpty = items.findIndex(i => !i.description);
    if (firstEmpty >= 0) {
      setItems(prev => prev.map((item, i) => i === firstEmpty ? newItem : item));
    } else {
      setItems(prev => [...prev, newItem]);
    }
  };

  useEffect(() => {
    api.get('/vendors').then(res => setVendors(res.data.data?.vendors ?? res.data.data ?? [])).catch(() => {});
    if (!isEdit) return;
    const controller = new AbortController();
    setLoading(true);
    // Replace (not append) — clear first so stale items from a previous PO never
    // persist if React Router reuses this component across route changes.
    setItems([]);
    api.get(`/purchase-orders/${id}`, { signal: controller.signal }).then(res => {
      const po = res.data.data;
      setVendorId(po.vendorId || po.vendor?.id || '');
      setVendorGstin(po.vendor?.gstin || '');
      setStatus(po.status || 'draft');
      setDeliveryDate(po.deliveryDate ? po.deliveryDate.split('T')[0] : '');
      setNotes(po.notes || '');
      setBillingAddress(po.billingAddress || { line1: '', line2: '', city: '', state: '', pincode: '', country: 'India' });
      setDeliveryAddress(po.deliveryAddress || { line1: '', line2: '', city: '', state: '', pincode: '', country: 'India' });
      setPickupAddress(po.pickupAddress || { line1: '', line2: '', city: '', state: '', pincode: '', country: 'India' });
      if (po.discountType) setDocDiscountType(po.discountType);
      if (po.discountValue != null) setDocDiscountValue(Number(po.discountValue));
      const loadedItems = (po.items || []).map((item: any) => ({
        description: item.description || '',
        hsnSac: item.hsnSac || '',
        quantity: item.quantity || 1,
        rate: item.rate || 0,
        gstRate: item.gstRate ?? 18,
        discount: Number(item.discount) || 0,
        productId: item.productId || '',
      }));
      // REPLACE (not append) — assign directly, never spread into prev state
      setItems(loadedItems.length > 0 ? loadedItems : [emptyItem()]);
    }).catch(err => {
      if (err.code === 'ERR_CANCELED') return; // aborted by cleanup — not an error
      alert('Failed to load purchase order');
      navigate('/purchase-orders');
    }).finally(() => setLoading(false));
    // Cleanup: abort the in-flight request when id changes or component unmounts.
    // Without this, a stale response from PO A could resolve after PO B loads
    // and overwrite B's items — producing the duplicate line-item bug.
    return () => controller.abort();
  }, [id, isEdit]);

  const openGrnModal = async () => {
    try {
      const res = await api.get(`/purchase-orders/${id}`);
      const po = res.data.data;
      setGrnItems((po.items || []).map((item: any) => ({
        id: item.id,
        description: item.description || '',
        orderedQty: Number(item.quantity) || 0,
        receivedQty: Number(item.quantity) || 0,
      })));
      setGrnNotes('');
      setShowGrn(true);
    } catch {
      alert('Failed to load PO items for GRN');
    }
  };

  const handleGrnSubmit = async () => {
    const validItems = grnItems.filter(i => i.receivedQty > 0);
    if (validItems.length === 0) { alert('Enter received qty for at least one item'); return; }
    try {
      setGrnSaving(true);
      await api.post(`/purchase-orders/${id}/grn`, {
        items: validItems.map(i => ({ itemId: i.id, receivedQty: i.receivedQty })),
        notes: grnNotes || undefined,
      });
      setShowGrn(false);
      navigate('/purchase-orders');
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to create GRN');
    } finally {
      setGrnSaving(false);
    }
  };

  const updateItem = (idx: number, field: keyof LineItem, value: string | number) => {
    setItems(prev => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  };

  const removeItem = (idx: number) => {
    if (items.length <= 1) return;
    setItems(prev => prev.filter((_, i) => i !== idx));
  };

  const totals = useMemo(() => {
    let subtotal = 0;
    let totalGst = 0;
    let totalItemDiscount = 0;

    items.forEach(item => {
      const grossAmount = Number((item.quantity * item.rate).toFixed(2));
      const discountAmount = Number((grossAmount * (item.discount || 0) / 100).toFixed(2));
      const taxableAmount = Number((grossAmount - discountAmount).toFixed(2));
      const lineGst = Number((taxableAmount * (item.gstRate / 100)).toFixed(2));

      subtotal += taxableAmount;
      totalGst += lineGst;
      totalItemDiscount += discountAmount;
    });

    subtotal = Number(subtotal.toFixed(2));
    totalGst = Number(totalGst.toFixed(2));
    totalItemDiscount = Number(totalItemDiscount.toFixed(2));

    // Document-level vendor discount
    let docDiscountAmt = 0;
    let docDiscountPct = 0;
    if (docDiscountType === 'percentage') {
      docDiscountPct = docDiscountValue;
      docDiscountAmt = Number((subtotal * docDiscountValue / 100).toFixed(2));
    } else {
      docDiscountAmt = Number(docDiscountValue.toFixed(2));
      docDiscountPct = subtotal > 0 ? Number((docDiscountValue / subtotal * 100).toFixed(2)) : 0;
    }
    const afterDocDiscount = Number((subtotal - docDiscountAmt).toFixed(2));

    return { subtotal, totalGst, total: Number((afterDocDiscount + totalGst).toFixed(2)), totalItemDiscount, docDiscountAmt, docDiscountPct, afterDocDiscount };
  }, [items, docDiscountType, docDiscountValue]);

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = {
        vendorId,
        deliveryDate: deliveryDate || undefined,
        notes: notes || undefined,
        billingAddress: billingAddress,
        deliveryAddress: deliveryAddress,
        pickupAddress: pickupAddress,
        discountType: docDiscountType,
        discountValue: docDiscountValue,
        discountPct: totals.docDiscountPct,
        discountAmount: totals.docDiscountAmt,
        items: items.filter(i => i.description).map(i => ({
          description: i.description,
          hsnSac: i.hsnSac || undefined,
          quantity: i.quantity,
          rate: i.rate,
          gstRate: i.gstRate,
          discount: i.discount || 0,
          productId: i.productId || undefined,
        })),
      };

      if (isEdit) {
        await api.put(`/purchase-orders/${id}`, payload);
      } else {
        await api.post('/purchase-orders', payload);
      }
      navigate('/purchase-orders');
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to save purchase order');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
        <span className="ml-2 text-sm text-gray-500">Loading purchase order...</span>
      </div>
    );
  }

  const inp = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]';
  const lbl = 'mb-1 block text-sm font-medium text-gray-700';

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center gap-4">
        <button onClick={() => navigate('/purchase-orders')} className="rounded-lg border border-gray-300 p-2 text-gray-500 hover:bg-gray-50">
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">{isEdit ? 'Edit Purchase Order' : 'New Purchase Order'}</h1>
        </div>
        {isEdit && status === 'confirmed' && (
          <button onClick={openGrnModal}
            className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700">
            <PackageCheck size={14} />
            Create GRN
          </button>
        )}
        <button onClick={handleSave} disabled={saving || !vendorId || items.filter(i => i.description).length === 0}
          className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {isEdit ? 'Update' : 'Create'} Purchase Order
        </button>
      </div>

      {/* Form */}
      <div className="space-y-6">
        <div className="rounded-xl border border-gray-200 bg-white p-6">
          <h2 className="mb-4 text-lg font-semibold text-gray-900">Order Details</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className={lbl}>Vendor *</label>
              <select value={vendorId} onChange={e => { const vid = e.target.value; setVendorId(vid); setVendorGstin(vendors.find(v => v.id === vid)?.gstin || ''); }} className={inp}>
                <option value="">Select Vendor</option>
                {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </div>
            <div>
              <label className={lbl}>Delivery Date</label>
              <input type="date" value={deliveryDate} onChange={e => setDeliveryDate(e.target.value)} className={inp} />
            </div>
            <div>
              <label className={lbl}>Notes</label>
              <input type="text" value={notes} onChange={e => setNotes(e.target.value)} className={inp} />
            </div>
            <div>
              <label className={lbl}>Vendor GSTIN</label>
              <input type="text" value={vendorGstin} readOnly placeholder="Auto-filled on vendor selection"
                className={`${inp} bg-gray-50 text-gray-500 cursor-not-allowed`} />
            </div>
          </div>
        </div>

        {/* Addresses */}
        <div className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-6">
          <h2 className="mb-4 text-lg font-semibold text-[var(--text-primary)]">Addresses</h2>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {/* Vendor Billing Address */}
            <div>
              <h3 className="mb-2 text-xs font-medium text-[var(--text-secondary)]">Vendor Billing Address</h3>
              <div className="space-y-2">
                <input type="text" placeholder="Address Line 1" value={billingAddress?.line1 || ''} onChange={e => setBillingAddress({...billingAddress, line1: e.target.value})} className={inp} />
                <input type="text" placeholder="Address Line 2" value={billingAddress?.line2 || ''} onChange={e => setBillingAddress({...billingAddress, line2: e.target.value})} className={inp} />
                <div className="grid grid-cols-2 gap-2">
                  <input type="text" placeholder="City" value={billingAddress?.city || ''} onChange={e => setBillingAddress({...billingAddress, city: e.target.value})} className={inp} />
                  <input type="text" placeholder="State" value={billingAddress?.state || ''} onChange={e => setBillingAddress({...billingAddress, state: e.target.value})} className={inp} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <input type="text" placeholder="Pincode" value={billingAddress?.pincode || ''} onChange={e => setBillingAddress({...billingAddress, pincode: e.target.value})} className={inp} />
                  <input type="text" placeholder="Country" value={billingAddress?.country || 'India'} onChange={e => setBillingAddress({...billingAddress, country: e.target.value})} className={inp} />
                </div>
              </div>
            </div>
            {/* Pickup Address (Vendor's factory/warehouse) */}
            <div>
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-xs font-medium text-[var(--text-secondary)]">Pickup Address</h3>
                <button type="button" onClick={() => setPickupAddress({...billingAddress})} className="text-[10px] text-[var(--ink-0)] hover:underline">Copy from Billing</button>
              </div>
              <div className="space-y-2">
                <input type="text" placeholder="Factory/Warehouse Address" value={pickupAddress?.line1 || ''} onChange={e => setPickupAddress({...pickupAddress, line1: e.target.value})} className={inp} />
                <input type="text" placeholder="Address Line 2" value={pickupAddress?.line2 || ''} onChange={e => setPickupAddress({...pickupAddress, line2: e.target.value})} className={inp} />
                <div className="grid grid-cols-2 gap-2">
                  <input type="text" placeholder="City" value={pickupAddress?.city || ''} onChange={e => setPickupAddress({...pickupAddress, city: e.target.value})} className={inp} />
                  <input type="text" placeholder="State" value={pickupAddress?.state || ''} onChange={e => setPickupAddress({...pickupAddress, state: e.target.value})} className={inp} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <input type="text" placeholder="Pincode" value={pickupAddress?.pincode || ''} onChange={e => setPickupAddress({...pickupAddress, pincode: e.target.value})} className={inp} />
                  <input type="text" placeholder="Country" value={pickupAddress?.country || 'India'} onChange={e => setPickupAddress({...pickupAddress, country: e.target.value})} className={inp} />
                </div>
              </div>
            </div>
            {/* Delivery Address (Our warehouse/site) */}
            <div>
              <h3 className="mb-2 text-xs font-medium text-[var(--text-secondary)]">Delivery Address (Our Site)</h3>
              <div className="space-y-2">
                <input type="text" placeholder="Warehouse/Site Address" value={deliveryAddress?.line1 || ''} onChange={e => setDeliveryAddress({...deliveryAddress, line1: e.target.value})} className={inp} />
                <input type="text" placeholder="Address Line 2" value={deliveryAddress?.line2 || ''} onChange={e => setDeliveryAddress({...deliveryAddress, line2: e.target.value})} className={inp} />
                <div className="grid grid-cols-2 gap-2">
                  <input type="text" placeholder="City" value={deliveryAddress?.city || ''} onChange={e => setDeliveryAddress({...deliveryAddress, city: e.target.value})} className={inp} />
                  <input type="text" placeholder="State" value={deliveryAddress?.state || ''} onChange={e => setDeliveryAddress({...deliveryAddress, state: e.target.value})} className={inp} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <input type="text" placeholder="Pincode" value={deliveryAddress?.pincode || ''} onChange={e => setDeliveryAddress({...deliveryAddress, pincode: e.target.value})} className={inp} />
                  <input type="text" placeholder="Country" value={deliveryAddress?.country || 'India'} onChange={e => setDeliveryAddress({...deliveryAddress, country: e.target.value})} className={inp} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Line Items */}
        <div className="rounded-xl border border-gray-200 bg-white p-6">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Line Items</h2>
            <div className="flex items-center gap-3">
              <button onClick={() => setShowProducts(true)} className="flex items-center gap-1.5 rounded-lg border border-[var(--ink-3)] bg-[var(--saffron-50)] px-3 py-1.5 text-sm font-medium text-[var(--ink-0)] hover:bg-[var(--saffron-50)] transition-colors">
                <ShoppingBag size={14} /> Browse Products
              </button>
              <button onClick={() => setItems([...items, emptyItem()])} className="flex items-center gap-1 text-sm text-[var(--ink-0)] hover:underline">
                <Plus size={14} /> Add Item
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs font-medium uppercase text-gray-500">
                  <th className="px-2 py-2">Description</th>
                  <th className="px-2 py-2 w-24">HSN/SAC</th>
                  <th className="px-2 py-2 w-20">Qty</th>
                  <th className="px-2 py-2 w-24">Rate</th>
                  <th className="px-2 py-2 w-20">Disc %</th>
                  <th className="px-2 py-2 w-20">GST %</th>
                  <th className="px-2 py-2 w-24 text-right">Amount</th>
                  <th className="px-2 py-2 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => {
                  const grossAmount = Number((item.quantity * item.rate).toFixed(2));
                  const discAmount = Number((grossAmount * (item.discount || 0) / 100).toFixed(2));
                  const lineAmount = Number((grossAmount - discAmount).toFixed(2));
                  const lineGst = Number((lineAmount * (item.gstRate / 100)).toFixed(2));
                  return (
                    <tr key={idx} className="border-b border-gray-100">
                      <td className="px-2 py-2">
                        <input type="text" value={item.description} onChange={e => updateItem(idx, 'description', e.target.value)} placeholder="Item description"
                          className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-[var(--ink-0)] focus:outline-none" />
                      </td>
                      <td className="px-2 py-2">
                        <input type="text" value={item.hsnSac} onChange={e => updateItem(idx, 'hsnSac', e.target.value)}
                          className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-[var(--ink-0)] focus:outline-none" />
                      </td>
                      <td className="px-2 py-2">
                        <input type="number" value={item.quantity} onChange={e => updateItem(idx, 'quantity', parseFloat(e.target.value) || 0)}
                          className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-[var(--ink-0)] focus:outline-none" />
                      </td>
                      <td className="px-2 py-2">
                        <input type="number" value={item.rate} onChange={e => updateItem(idx, 'rate', parseFloat(e.target.value) || 0)}
                          className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-[var(--ink-0)] focus:outline-none" />
                      </td>
                      <td className="px-2 py-2">
                        <input type="number" min={0} max={100} step="0.5" value={item.discount || ''} onChange={e => updateItem(idx, 'discount', parseFloat(e.target.value) || 0)}
                          placeholder="0" className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm text-center focus:border-[var(--ink-0)] focus:outline-none" />
                      </td>
                      <td className="px-2 py-2">
                        <select value={item.gstRate} onChange={e => updateItem(idx, 'gstRate', parseInt(e.target.value))}
                          className="w-full rounded border border-gray-300 bg-white px-2 py-1.5 text-sm focus:border-[var(--ink-0)] focus:outline-none">
                          {GST_RATES.map(r => <option key={r} value={r}>{r}%</option>)}
                        </select>
                      </td>
                      <td className="px-2 py-2 text-right font-medium text-gray-900">{formatINR(lineAmount + lineGst)}</td>
                      <td className="px-2 py-2">
                        {items.length > 1 && (
                          <button onClick={() => removeItem(idx)} className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600">
                            <Trash2 size={14} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="mt-4 flex justify-end">
            <div className="w-64 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Subtotal</span>
                <span className="font-medium text-gray-900">{formatINR(totals.subtotal)}</span>
              </div>
              {totals.totalItemDiscount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Item Discounts</span>
                  <span className="font-medium text-green-700">-{formatINR(totals.totalItemDiscount)}</span>
                </div>
              )}
              {totals.docDiscountAmt > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Vendor Discount ({totals.docDiscountPct?.toFixed(1)}%)</span>
                  <span className="font-medium text-green-700">-{formatINR(totals.docDiscountAmt)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">GST</span>
                <span className="font-medium text-gray-900">{formatINR(totals.totalGst)}</span>
              </div>
              <div className="flex justify-between border-t border-gray-200 pt-2 text-base">
                <span className="font-semibold text-gray-900">Total</span>
                <span className="font-bold text-gray-900">{formatINR(totals.total)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Vendor Discount */}
        <div className="rounded-lg border border-dashed border-green-300 bg-green-50 p-4">
          <div className="flex items-center gap-3 mb-2">
            <h4 className="text-sm font-semibold text-green-800">Vendor Discount</h4>
            <div className="flex rounded-lg border border-green-300 overflow-hidden">
              <button onClick={() => setDocDiscountType('amount')} className={`px-3 py-1 text-xs font-medium ${docDiscountType === 'amount' ? 'bg-green-600 text-white' : 'text-green-700 hover:bg-green-100'}`}>
                &#8377; Amount
              </button>
              <button onClick={() => setDocDiscountType('percentage')} className={`px-3 py-1 text-xs font-medium ${docDiscountType === 'percentage' ? 'bg-green-600 text-white' : 'text-green-700 hover:bg-green-100'}`}>
                % Percentage
              </button>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <input type="number" min="0" value={docDiscountValue || ''} onChange={e => setDocDiscountValue(parseFloat(e.target.value) || 0)}
              placeholder={docDiscountType === 'amount' ? 'Discount amount' : '% Discount'}
              className="w-48 rounded-lg border border-green-300 px-3 py-2 text-sm" />
            <span className="text-sm text-green-700">
              {docDiscountType === 'amount'
                ? `(${totals.docDiscountPct?.toFixed(2) || 0}%)`
                : `(${formatINR(totals.docDiscountAmt || 0)})`}
            </span>
          </div>
        </div>
      </div>

      {/* Product Grid Selector */}
      <ProductGridSelector open={showProducts} onClose={() => setShowProducts(false)} onSelect={handleProductSelect} />

      {/* GRN Modal */}
      {showGrn && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowGrn(false)}>
          <div className="mx-4 w-full max-w-xl rounded-xl bg-white shadow-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 z-10 bg-white rounded-t-xl flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <h2 className="text-lg font-semibold text-gray-900">Create Goods Receipt Note</h2>
              <button onClick={() => setShowGrn(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <div className="space-y-4 px-6 py-4">
              <p className="text-sm text-gray-500">Enter the quantity received for each item.</p>
              <div className="space-y-3">
                {grnItems.map((item, idx) => (
                  <div key={item.id} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                    <p className="mb-2 text-sm font-medium text-gray-800">{item.description}</p>
                    <div className="flex items-center gap-4">
                      <div className="flex-1">
                        <label className="mb-0.5 block text-xs text-gray-500">Ordered Qty</label>
                        <input type="number" value={item.orderedQty} readOnly
                          className="w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm text-gray-400 cursor-not-allowed" />
                      </div>
                      <div className="flex-1">
                        <label className="mb-0.5 block text-xs text-gray-500">Received Qty *</label>
                        <input type="number" min={0} max={item.orderedQty} value={item.receivedQty}
                          onChange={e => setGrnItems(prev => prev.map((g, i) => i === idx ? { ...g, receivedQty: parseFloat(e.target.value) || 0 } : g))}
                          className="w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm focus:border-[var(--ink-0)] focus:outline-none" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Notes</label>
                <input type="text" value={grnNotes} onChange={e => setGrnNotes(e.target.value)} placeholder="Optional notes"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none" />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <button onClick={() => setShowGrn(false)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={handleGrnSubmit} disabled={grnSaving}
                className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50">
                {grnSaving && <Loader2 size={14} className="animate-spin" />}
                Confirm Receipt
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
